import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DefinitionTooltip,
  TablePagePagination,
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  PAID_MEDIA_TAG_ROLE_ADSET,
  PAID_MEDIA_TAG_ROLE_CAMPAIGN,
  googleAdsAdNetworkChannelClass,
} from "../../utils/paidMediaTags";
import {
  displayFormattedCurrency,
  getGoogleColumnTooltip,
  getRoasToneClass,
  hasDisplayValue,
  type GoogleAdsAdsSortKey,
  type GoogleSectionHelpKey,
  type LiveAdRow,
} from "./googleAdsTableConfig";
import { formatPaidMediaRoas } from "../../utils/paidMediaNumberFormat";
import "./GoogleAdsTable.scss";

interface GoogleAdsTableProps {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  adsSearch: string;
  setAdsSearch: Dispatch<SetStateAction<string>>;
  setAdsPage: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  exportAdsCsv: () => void;
  toggleAdsSort: (_key: GoogleAdsAdsSortKey) => void;
  adRows: LiveAdRow[];
  formatCountMetric: (_value: number | null | undefined) => string;
  formatConversionMetric: (_value: number | null | undefined) => string;
  formatNullablePercent: (_value: number | null | undefined) => string;
  adsPage: number;
  adsPageCount: number;
}

const GoogleAdsTable: FC<GoogleAdsTableProps> = ({
  googleSectionHelp,
  adsSearch,
  setAdsSearch,
  setAdsPage,
  isExporting,
  exportAdsCsv,
  toggleAdsSort,
  adRows,
  formatCountMetric,
  formatConversionMetric,
  formatNullablePercent,
  adsPage,
  adsPageCount,
}) => {
  const { t } = useTranslation();

  return (
    <DashboardDataTablePanel className="google-ads-ads-table-panel">
      <DashboardDataTablePanelHeader>
        <div className="paid-media-panel__heading">
          <h3>{t("googleAdsReport.sections.allAds")}</h3>
          <DefinitionTooltip
            label={t("googleAdsReport.sections.allAds")}
            info={googleSectionHelp("allAds")}
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
        <div className="paid-media-table-toolbar" role="toolbar" aria-label={t("googleAdsReport.toolbars.adsSearch")}>
          <label className={`${dt.search} paid-media-table-toolbar__search`}>
            <Search size={16} />
            <input
              type="search"
              value={adsSearch}
              placeholder={t("googleAdsReport.placeholders.searchAds")}
              onChange={event => {
                setAdsSearch(event.target.value);
                setAdsPage(0);
              }}
            />
          </label>
          <button
            type="button"
            className="paid-media-export-btn"
            disabled={isExporting}
            onClick={() => exportAdsCsv()}
          >
            <Download size={16} aria-hidden />
            {isExporting ? t("googleAdsReport.export.exporting") : t("googleAdsReport.export.csv")}
          </button>
        </div>
      </DashboardDataTablePanelHeader>
      <DashboardDataTableScroll>
        <DashboardDataTableTable>
          <thead>
            <tr>
              <th className="paid-media-report__ad-col">
                <DashboardDataTableSortButton onClick={() => toggleAdsSort("name")}>
                  {t("googleAdsReport.adsTable.adName")}
                </DashboardDataTableSortButton>
              </th>
              {(
                [
                  ["type", ""],
                  ["spend", dt.number],
                  ["impressions", dt.number],
                  ["clicks", dt.number],
                  ["conversions", dt.number],
                  ["ctr", dt.number],
                  ["cpc", dt.number],
                  ["cpa", dt.number],
                  ["roas", dt.number],
                ] as const
              ).map(([column, alignClass], columnIndex, columns) => (
                <th key={column} className={alignClass || undefined}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton onClick={() => toggleAdsSort(column)}>
                      {t(`googleAdsReport.adsTable.${column}`)}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t(`googleAdsReport.adsTable.${column}`)}
                      info={getGoogleColumnTooltip(t, column, "ad")}
                      align={columnIndex >= columns.length - 3 ? "end" : "start"}
                      formulaTitle={t("webAnalytics.kpi.formula")}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {adRows.map(row => (
              <tr key={row.id}>
                <td className="paid-media-report__ad-col">
                  <div className="paid-media-creative paid-media-creative--inline">
                    <div>
                      <strong className="paid-media-creative__title-inline">{row.name}</strong>
                      <div className="paid-media-tag-row">
                        {hasDisplayValue(row.campaignName) ? (
                          <span className={`paid-media-mini-tag ${PAID_MEDIA_TAG_ROLE_CAMPAIGN}`}>
                            {row.campaignName}
                          </span>
                        ) : null}
                        {hasDisplayValue(row.adGroupName) ? (
                          <span className={`paid-media-mini-tag ${PAID_MEDIA_TAG_ROLE_ADSET}`}>
                            {row.adGroupName}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`paid-media-mini-tag ${googleAdsAdNetworkChannelClass(row.type)}`}>
                    {row.type}
                  </span>
                </td>
                <td className={dt.number}>{displayFormattedCurrency(row.spendFormatted)}</td>
                <td className={dt.number}>{formatCountMetric(row.impressions)}</td>
                <td className={dt.number}>{formatCountMetric(row.clicks)}</td>
                <td className={dt.number}>{formatConversionMetric(row.conversions)}</td>
                <td className={dt.number}>{formatNullablePercent(row.ctr)}</td>
                <td className={dt.number}>{displayFormattedCurrency(row.cpcFormatted)}</td>
                <td className={dt.number}>{displayFormattedCurrency(row.cpaFormatted)}</td>
                <td className={`${dt.number} ${getRoasToneClass(row.roas)}`}>{formatPaidMediaRoas(row.roas)}</td>
              </tr>
            ))}
          </tbody>
        </DashboardDataTableTable>
      </DashboardDataTableScroll>
      <TablePagePagination
        currentPage={adsPage + 1}
        totalPages={adsPageCount}
        onPrevious={() => setAdsPage(p => Math.max(0, p - 1))}
        onNext={() => setAdsPage(p => Math.min(adsPageCount - 1, p + 1))}
      />
    </DashboardDataTablePanel>
  );
};

export default GoogleAdsTable;
